import { requestGQL } from 'common/graphQlClient';
import { useQueryClient } from 'react-query';
import { useOfflineMutation } from 'common/offline/useOfflineMutation';

import { UPDATE_OUTLETS_PHOTOS } from '../api';

type UploadOutletPhotoPayload = {
  propertyId: number;
  photoUrl: string;
  outletId: string;
};

/**
 * Hook for updating outlet photos with offline support.
 *
 * When offline, the mutation is queued and will be sent when back online.
 * The photo URL update is stored locally and synced later.
 */
export const useMutateOutletPhoto = (propertyId: number | null) => {
  const queryClient = useQueryClient();

  const mutation = useOfflineMutation<unknown, { message: string }, UploadOutletPhotoPayload>({
    mutationFn: async (payload) => requestGQL(UPDATE_OUTLETS_PHOTOS, payload),
    mutationName: 'uploadOutletPhoto',
    graphqlDocument: UPDATE_OUTLETS_PHOTOS,
    entity: 'outletPhoto',
    operationType: 'UPDATE',
    propertyId: propertyId ?? undefined,
    invalidateKeys: [['propertyConnection', propertyId]],
    // Optimistic update - add photo URL to local cache immediately
    getOptimisticData: (payload) => payload,
    applyOptimisticUpdate: (optimisticData) => {
      const data = optimisticData as UploadOutletPhotoPayload;
      const cacheKey = ['propertyConnection', propertyId];

      queryClient.setQueryData(cacheKey, (oldData: any) => {
        if (!oldData?.propertyById?.connectionByConnectionId?.serialized2?.nodeDataArray) {
          return oldData;
        }

        // Find and update the outlet with the new photo
        const nodeDataArray = oldData.propertyById.connectionByConnectionId.serialized2.nodeDataArray.map(
          (node: any) => {
            if (node.id === data.outletId && node.model === 'outlet') {
              return {
                ...node,
                cfObject: {
                  ...node.cfObject,
                  images: [...(node.cfObject?.images || []), data.photoUrl],
                },
              };
            }
            return node;
          },
        );

        return {
          ...oldData,
          propertyById: {
            ...oldData.propertyById,
            connectionByConnectionId: {
              ...oldData.propertyById.connectionByConnectionId,
              serialized2: {
                ...oldData.propertyById.connectionByConnectionId.serialized2,
                nodeDataArray,
              },
            },
          },
        };
      });
    },
  });

  return mutation;
};
